import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Divider } from "./Ornaments";
import { CHOWKI, MAP_EMBEDS, WEDDING } from "./data";

const copyAddress = async (address, label) => {
    try {
        await navigator.clipboard.writeText(`${label}, ${address}`);
        toast.success("Address copied to clipboard");
    } catch {
        toast.error("Could not copy address");
    }
};

const MapCard = ({ venue, embedSrc, mapTestid, copyTestid, directionsTestid, delay }) => (
    <Reveal delay={delay}>
        <article className="overflow-hidden rounded-2xl border border-gold/20 bg-royal-card/70 transition-colors duration-700 hover:border-gold/50">
            <div className="px-6 pb-5 pt-7 text-center sm:px-8">
                <p className="font-devanagari text-sm text-gold/90">{venue.invocation}</p>
                <h3 className="mt-2 font-royal text-2xl font-semibold text-ivory sm:text-3xl">
                    {venue.venue}
                </h3>
                <p className="mt-2 font-cinzel text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                    {venue.date} · {venue.time || "6:30 PM onwards"}
                </p>
            </div>
            <div className="px-4 sm:px-6">
                <iframe
                    data-testid={mapTestid}
                    title={`Map — ${venue.venue}`}
                    src={embedSrc}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    className="h-72 w-full rounded-xl border border-gold/25 sm:h-80"
                />
            </div>
            <div className="px-6 pb-8 pt-5 text-center sm:px-8">
                <p className="mx-auto max-w-md font-royal text-base italic leading-relaxed text-sand">
                    {venue.address}
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3.5">
                    <a
                        data-testid={directionsTestid}
                        href={venue.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3 font-cinzel text-[10px] font-bold uppercase tracking-[0.28em] text-obsidian transition-transform duration-500 hover:scale-[1.05]"
                    >
                        <ExternalLink size={13} />
                        Open In Google Maps
                    </a>
                    <button
                        data-testid={copyTestid}
                        onClick={() => copyAddress(venue.address, venue.venue)}
                        className="inline-flex items-center gap-2.5 rounded-full border border-gold/40 px-7 py-3 font-cinzel text-[10px] font-bold uppercase tracking-[0.28em] text-gold transition-colors duration-500 hover:bg-gold/10"
                    >
                        <Copy size={13} />
                        Copy Address
                    </button>
                </div>
            </div>
        </article>
    </Reveal>
);

const Venues = () => (
    <section
        id="venues"
        data-testid="venues-section"
        className="relative border-t border-gold/10 bg-royal-surface/30 py-24 sm:py-36"
    >
        <div className="mx-auto max-w-6xl px-6">
            <Reveal>
                <p className="text-center font-cinzel text-[11px] font-semibold uppercase tracking-[0.5em] text-gold">
                    Find Your Way
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="mt-6 text-center font-royal text-3xl font-medium text-ivory sm:text-4xl">
                    Live Maps To Both Celebrations
                </h2>
            </Reveal>
            <Reveal delay={0.15}>
                <Divider className="mb-16 mt-8" />
            </Reveal>

            <div className="grid gap-10 lg:grid-cols-2 lg:gap-12">
                <MapCard
                    venue={CHOWKI}
                    embedSrc={MAP_EMBEDS.chowki}
                    mapTestid="map-iframe-chowki"
                    copyTestid="copy-address-chowki"
                    directionsTestid="venue-directions-chowki"
                    delay={0.2}
                />
                <MapCard
                    venue={{ ...WEDDING, time: null }}
                    embedSrc={MAP_EMBEDS.wedding}
                    mapTestid="map-iframe-wedding"
                    copyTestid="copy-address-wedding"
                    directionsTestid="venue-directions-wedding"
                    delay={0.3}
                />
            </div>
        </div>
    </section>
);

export default Venues;
